import { LockManager } from 'navigator.locks/src/polyfill';

/**
 * Contextualizes a log message for better clarity in debugging.
 * @param message - The message to be contextualized.
 * @returns A string that includes the context of the polyfills.
 */
const contextualizedLogMessage = (message: unknown) => `polyfills. ${message}`;

/**
 * Registers necessary polyfills for the authentication functionality.
 * @description
 * This function checks if the `navigator.locks` property is defined. If it is not
 * defined, it attempts to define it using a `LockManager` polyfill from the 'navigator.locks' package.
 * @returns
 */
export const registerAuthPolyFills = () => {
  try {
    if (!navigator) {
      console.error(
        contextualizedLogMessage(
          'synchronizedSessionRefresh: Navigator is undefined. Cannot register auth polyfills.'
        )
      );
      return;
    }

    if (!navigator.locks) {
      console.debug(
        contextualizedLogMessage(
          'synchronizedSessionRefresh: Navigator locks property is undefined. Attempting to register polyfill.'
        ),
        navigator.locks
      );

      Object.defineProperty(navigator, 'locks', {
        value: new LockManager(),
        configurable: true,
      });

      console.debug(
        contextualizedLogMessage(
          'synchronizedSessionRefresh: Navigator locks property is now defined'
        ),
        navigator.locks
      );
    }
  } catch (err: unknown) {
    console.error(
      contextualizedLogMessage(
        'Error registering auth polyfills. This is unexpected and should not happen.'
      ),
      err
    );
  }
};

/**
 * Registers necessary polyfills for the application and throws no errors under any circumstances.
 * @description
 * IMPORTANT DEVELOPER NOTE: This function in its current implementation must not propagate any errors.
 * This function attempts to register functionality that doesn't exist in older browsers and that might be desired or required for
 * concrete product use cases.
 * It's EXTREMELY important that this function does nothing unless explicitly required for clear product use cases.
 */
export const registerPolyfills = () => {
  try {
    console.debug(
      contextualizedLogMessage('Registering polyfills if applicable')
    );

    registerAuthPolyFills();

    console.debug(contextualizedLogMessage('Registering polyfills finished'));
  } catch (err) {
    console.error(
      contextualizedLogMessage(
        'Error registering polyfills. This is unexpected and should not happen.'
      ),
      err
    );
  }
};
